import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Link2, AlertTriangle, CheckCircle, Loader2, UserPlus, Users } from 'lucide-react';
import { toast } from 'sonner';

export default function RiconciliazioneMMG({ onChanged }) {
  const [dati, setDati] = useState(null);
  const [medici, setMedici] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inCorso, setInCorso] = useState('');
  const [scelte, setScelte] = useState({});
  const [creaPer, setCreaPer] = useState(null);
  const [formCrea, setFormCrea] = useState({ cognome: '', nome: '', telefono: '', email: '', indirizzo_ambulatorio: '' });

  const carica = useCallback(async () => {
    setLoading(true);
    try {
      const [r, m] = await Promise.all([
        api.get('/utility/mmg/riconciliazione'),
        api.get('/utility/mmg'),
      ]);
      setDati(r.data);
      setMedici(m.data);
      const iniziali = {};
      r.data.gruppi.forEach(g => {
        const id = g.medico_collegato?.id || (g.proposta?.esatta ? g.proposta.id : '');
        if (id) iniziali[g.chiave] = id;
      });
      setScelte(iniziali);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore caricamento riconciliazione'); }
    setLoading(false);
  }, []);

  useEffect(() => { carica(); }, [carica]);

  const collega = async (gruppo, mmgId) => {
    setInCorso(gruppo.chiave);
    try {
      const r = await api.post('/utility/mmg/riconciliazione/collega', { testo: gruppo.testo, mmg_id: mmgId });
      toast.success(r.data.message);
      carica();
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore collegamento'); }
    setInCorso('');
  };

  const collegaEsatti = async () => {
    setInCorso('__esatti__');
    try {
      const r = await api.post('/utility/mmg/riconciliazione/collega-esatti');
      toast.success(`${r.data.ospiti_collegati} ospiti collegati su ${r.data.gruppi_collegati} nomi`
        + (r.data.gruppi_da_confermare ? `; ${r.data.gruppi_da_confermare} nomi restano da confermare a mano` : ''));
      carica();
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore collegamento'); }
    setInCorso('');
  };

  const creaECollega = async () => {
    if (!formCrea.cognome.trim()) { toast.error('Inserisci almeno il cognome'); return; }
    setInCorso(creaPer.chiave);
    try {
      const r = await api.post('/utility/mmg/riconciliazione/collega', { testo: creaPer.testo, crea: formCrea });
      toast.success(r.data.message);
      setCreaPer(null);
      carica();
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore creazione medico'); }
    setInCorso('');
  };

  const apriCrea = (gruppo) => {
    const parole = gruppo.testo.replace(/dott\.?(ssa)?|dr\.?(ssa)?/gi, '').trim().split(/\s+/);
    setFormCrea({
      cognome: parole[0] || '', nome: parole.slice(1).join(' ') || '',
      telefono: '', email: '', indirizzo_ambulatorio: ''
    });
    setCreaPer(gruppo);
  };

  if (loading) return <Card><CardContent className="p-6 text-center text-sm text-slate-400">Caricamento riconciliazione...</CardContent></Card>;
  if (!dati) return null;

  return (
    <Card data-testid="riconciliazione-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2 flex-wrap">
          <Link2 className="w-5 h-5 text-brand" />Riconciliazione medici di base
          <Badge className="bg-emerald-100 text-emerald-700 text-[10px]" data-testid="ric-collegati">{dati.totale_collegati} ospiti collegati</Badge>
          {dati.totale_da_collegare > 0 && (
            <Badge className="bg-amber-100 text-amber-700 text-[10px]" data-testid="ric-da-collegare">{dati.totale_da_collegare} da collegare</Badge>
          )}
          {dati.senza_medico.length > 0 && (
            <Badge className="bg-red-100 text-red-700 text-[10px]" data-testid="ric-senza-medico">{dati.senza_medico.length} senza medico</Badge>
          )}
          {dati.totale_da_collegare > 0 && (
            <Button size="sm" className="ml-auto h-8 text-xs bg-brand hover:bg-brand-dark text-white"
              onClick={collegaEsatti} disabled={inCorso === '__esatti__'} data-testid="collega-esatti-btn">
              {inCorso === '__esatti__' ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />Collegamento...</>
                : 'Collega tutte le corrispondenze esatte'}
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-slate-500 leading-relaxed">
          Ogni riga e un nome di medico scritto nelle anagrafiche. Scegli il medico della rubrica e collega
          in un clic tutti i suoi ospiti: da quel momento conteggi, lista farmaci, ordini e scheda emergenza
          leggono lo stesso collegamento. Il testo originale letto dalle schede resta conservato.
        </p>

        <div className="space-y-2">
          {dati.gruppi.map(g => {
            const scelta = scelte[g.chiave] || '';
            const completo = g.da_collegare === 0 && g.medico_collegato;
            return (
              <div key={g.chiave} className={`border rounded-lg p-3 ${completo ? 'bg-emerald-50/50 border-emerald-200' : g.ambiguo ? 'bg-amber-50/50 border-amber-200' : 'bg-white'}`}
                data-testid={`ric-gruppo-${g.chiave.replace(/\s+/g, '-').toLowerCase()}`}>
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="min-w-[200px] flex-1">
                    <p className="text-sm font-medium text-slate-800">{g.testo}</p>
                    <p className="text-[11px] text-slate-500 flex items-center gap-1 mt-0.5">
                      <Users className="w-3 h-3" />{g.totale} ospiti
                      {g.collegati > 0 && <span className="text-emerald-600">· {g.collegati} collegati</span>}
                      {g.da_collegare > 0 && <span className="text-amber-600">· {g.da_collegare} da collegare</span>}
                    </p>
                    {g.ambiguo && (
                      <p className="text-[11px] text-amber-700 flex items-center gap-1 mt-1">
                        <AlertTriangle className="w-3 h-3" />{g.motivo_ambiguita} — da confermare a mano
                      </p>
                    )}
                    <p className="text-[11px] text-slate-400 mt-1 truncate">
                      {g.ospiti.slice(0, 4).map(o => o.nome).join(', ')}{g.ospiti.length > 4 ? ` +${g.ospiti.length - 4}` : ''}
                    </p>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    {completo ? (
                      <Badge className="bg-emerald-100 text-emerald-700 text-[10px] gap-1">
                        <CheckCircle className="w-3 h-3" />collegato a {g.medico_collegato.nome}
                      </Badge>
                    ) : (
                      <>
                        <Select value={scelta} onValueChange={v => setScelte(s => ({ ...s, [g.chiave]: v }))}>
                          <SelectTrigger className="h-8 w-60 text-xs" data-testid={`ric-select-${g.chiave.replace(/\s+/g, '-').toLowerCase()}`}>
                            <SelectValue placeholder={g.proposta ? `Proposta: ${g.proposta.nome}` : 'Scegli medico in rubrica'} />
                          </SelectTrigger>
                          <SelectContent className="max-h-72">
                            {g.proposta && (
                              <SelectItem value={g.proposta.id}>
                                {g.proposta.nome} {g.proposta.esatta ? '(corrispondenza esatta)' : `(simile ${Math.round(g.proposta.punteggio * 100)}%)`}
                              </SelectItem>
                            )}
                            {medici.filter(m => m.id !== g.proposta?.id).map(m => (
                              <SelectItem key={m.id} value={m.id}>{m.cognome} {m.nome}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button size="sm" className="h-8 text-xs bg-brand hover:bg-brand-dark text-white"
                          disabled={!scelta || inCorso === g.chiave}
                          onClick={() => collega(g, scelta)}
                          data-testid={`ric-collega-${g.chiave.replace(/\s+/g, '-').toLowerCase()}`}>
                          {inCorso === g.chiave ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Collega'}
                        </Button>
                        <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => apriCrea(g)}
                          data-testid={`ric-crea-${g.chiave.replace(/\s+/g, '-').toLowerCase()}`}>
                          <UserPlus className="w-3.5 h-3.5 mr-1" />Crea in rubrica
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {dati.senza_medico.length > 0 && (
          <div className="border border-red-200 bg-red-50/50 rounded-lg p-3" data-testid="ric-elenco-senza-medico">
            <p className="text-sm font-medium text-red-700 mb-1">Ospiti senza medico di base ({dati.senza_medico.length})</p>
            <p className="text-xs text-slate-600">{dati.senza_medico.map(o => o.nome).join(', ')}</p>
          </div>
        )}
      </CardContent>

      <Dialog open={!!creaPer} onOpenChange={o => !o && setCreaPer(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-base">Crea il medico in rubrica</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">Nome letto dall'anagrafica: <strong>{creaPer?.testo}</strong></p>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Cognome *</Label>
                <Input className="h-9 text-xs" value={formCrea.cognome} onChange={e => setFormCrea(f => ({ ...f, cognome: e.target.value }))} data-testid="crea-cognome" /></div>
              <div><Label className="text-xs">Nome</Label>
                <Input className="h-9 text-xs" value={formCrea.nome} onChange={e => setFormCrea(f => ({ ...f, nome: e.target.value }))} data-testid="crea-nome" /></div>
              <div><Label className="text-xs">Telefono</Label>
                <Input className="h-9 text-xs" value={formCrea.telefono} onChange={e => setFormCrea(f => ({ ...f, telefono: e.target.value }))} /></div>
              <div><Label className="text-xs">Email</Label>
                <Input className="h-9 text-xs" value={formCrea.email} onChange={e => setFormCrea(f => ({ ...f, email: e.target.value }))} /></div>
              <div className="col-span-2"><Label className="text-xs">Ambulatorio</Label>
                <Input className="h-9 text-xs" value={formCrea.indirizzo_ambulatorio} onChange={e => setFormCrea(f => ({ ...f, indirizzo_ambulatorio: e.target.value }))} /></div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setCreaPer(null)}>Annulla</Button>
              <Button className="bg-brand hover:bg-brand-dark text-white" onClick={creaECollega} data-testid="crea-e-collega">
                Crea e collega gli ospiti
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
